export interface TargetSummary {
  id: string;
  activity_id?: string;
  activity_name?: string;
  target_code: string;
  target_name: string;
  address?: string | null;
  status: string;
  submission_count?: number;
  photo_count?: number;
  last_uploaded_at?: string | null;
}

export interface ActivitySummary {
  id: string;
  code: string;
  name: string;
  status?: string;
  is_default?: number;
  created_at?: string;
  updated_at?: string | null;
}

export interface Stats {
  totalTargets: number;
  uploadedTargets: number;
  pendingTargets: number;
  photoTotal: number;
  todayPhotos: number;
}

export interface AccessCode {
  id: string;
  activity_id: string;
  plain_code: string;
  label: string;
  status: "active" | "disabled" | "revoked";
  expires_at?: string | null;
  max_uses?: number | null;
  used_count: number;
  created_at: string;
  revoked_at?: string | null;
}

export interface Submission {
  id: string;
  visitor_name: string;
  visit_status: string;
  remark?: string | null;
  status: string;
  photo_count: number;
  created_at: string;
  completed_at?: string | null;
}

export interface Photo {
  id: string;
  r2_key: string;
  public_url?: string;
  original_filename?: string | null;
  content_type?: string | null;
  size?: number | null;
  created_at: string;
}

export function adminPhotoUrl(photo: Photo, variant: "original" | "thumb" | "preview" = "original"): string {
  const source = photo.public_url || `/api/admin/photos/${encodeURIComponent(photo.id)}`;
  const transformSource = source.startsWith("/") ? source.slice(1) : source;
  if (variant === "thumb") return `/cdn-cgi/image/width=128,height=128,dpr=2,fit=cover,format=webp/${transformSource}`;
  if (variant === "preview") return `/cdn-cgi/image/width=1200,fit=scale-down,format=webp,quality=80/${transformSource}`;
  return source;
}

async function parseResponse<T>(response: Response): Promise<T> {
  const contentType = response.headers.get("content-type") ?? "";
  const data = contentType.includes("application/json") ? await response.json() : await response.text();
  if (!response.ok) {
    const message = typeof data === "object" && data && "error" in data ? String(data.error) : String(data);
    throw new Error(message || "请求失败");
  }
  return data as T;
}

export const api = {
  defaultActivity: () =>
    fetch("/api/public/activities/default").then(parseResponse<{ activity: ActivitySummary }>),
  activityById: (id: string) =>
    fetch(`/api/public/activities/${encodeURIComponent(id)}`).then(parseResponse<{ activity: ActivitySummary }>),
  resolveActivity: (code: string) =>
    fetch(`/api/public/activities/resolve?code=${encodeURIComponent(code)}`).then(parseResponse<{ activity: ActivitySummary }>),
  verifyCode: (activityId: string, code: string) =>
    fetch("/api/auth/verify", { method: "POST", body: JSON.stringify({ activityId, code }) }).then(parseResponse<{ ok: boolean }>),
  uploadSession: () =>
    fetch("/api/auth/session").then(parseResponse<{ activity: ActivitySummary; targets: TargetSummary[] }>),
  uploadLogout: () => fetch("/api/auth/logout", { method: "POST" }).then(parseResponse<{ ok: boolean }>),
  uploadTargets: () => fetch("/api/upload/targets").then(parseResponse<{ activity: ActivitySummary; targets: TargetSummary[] }>),
  createSubmission: (body: {
    targetId: string;
    visitorName: string;
    visitStatus: string;
    remark: string;
    photoCount: number;
  }) => fetch("/api/upload/submissions", { method: "POST", body: JSON.stringify(body) }).then(parseResponse<{ id: string; target: TargetSummary }>),
  uploadPhoto: (submissionId: string, file: File) => {
    const form = new FormData();
    form.append("photo", file);
    return fetch(`/api/upload/submissions/${submissionId}/photos`, { method: "POST", body: form }).then(parseResponse<{ id: string; key: string }>);
  },
  completeSubmission: (submissionId: string) =>
    fetch(`/api/upload/submissions/${submissionId}/complete`, { method: "POST" }).then(parseResponse<{ photoCount: number; targetPhotoCount: number }>),
  adminLogin: (password: string) =>
    fetch("/api/admin/login", { method: "POST", body: JSON.stringify({ password }) }).then(parseResponse<{ ok: boolean }>),
  adminSession: () =>
    fetch("/api/admin/session").then(parseResponse<{ ok: boolean }>),
  adminSetupStatus: () =>
    fetch("/api/admin/setup-status").then(parseResponse<{ setupRequired: boolean }>),
  setupAdminPassword: (password: string) =>
    fetch("/api/admin/setup", { method: "POST", body: JSON.stringify({ password }) }).then(parseResponse<{ ok: boolean }>),
  changeAdminPassword: (currentPassword: string, newPassword: string) =>
    fetch("/api/admin/password", { method: "POST", body: JSON.stringify({ currentPassword, newPassword }) }).then(parseResponse<{ ok: boolean }>),
  adminLogout: () => fetch("/api/admin/logout", { method: "POST" }).then(parseResponse<{ ok: boolean }>),
  stats: (activityId = "") => {
    const suffix = activityId ? `?activityId=${encodeURIComponent(activityId)}` : "";
    return fetch(`/api/admin/stats${suffix}`).then(parseResponse<Stats>);
  },
  activities: () => fetch("/api/admin/activities").then(parseResponse<{ activities: ActivitySummary[] }>),
  createActivity: (body: { name: string; code: string; isDefault?: boolean }) =>
    fetch("/api/admin/activities", { method: "POST", body: JSON.stringify(body) }).then(parseResponse<{ activity: ActivitySummary }>),
  updateActivity: (id: string, body: { name?: string; code?: string; status?: "active" | "disabled"; isDefault?: boolean }) =>
    fetch(`/api/admin/activities/${id}`, { method: "PATCH", body: JSON.stringify(body) }).then(parseResponse<{ activity: ActivitySummary }>),
  deleteActivity: (id: string) =>
    fetch(`/api/admin/activities/${id}`, { method: "DELETE" }).then(parseResponse<{ ok: boolean }>),
  targets: (query = "", status = "", activity = "", activityId = "") => {
    const params = new URLSearchParams();
    if (query) params.set("q", query);
    if (status) params.set("status", status);
    if (activity) params.set("activity", activity);
    if (activityId) params.set("activityId", activityId);
    const suffix = params.toString() ? `?${params}` : "";
    return fetch(`/api/admin/targets${suffix}`).then(parseResponse<{ targets: TargetSummary[] }>);
  },
  targetDetail: (id: string) =>
    fetch(`/api/admin/targets/${id}`).then(parseResponse<{ activity: ActivitySummary; target: TargetSummary; submissions: Submission[]; photos: Photo[] }>),
  deleteTarget: (id: string) =>
    fetch(`/api/admin/targets/${id}`, { method: "DELETE" }).then(parseResponse<{ ok: boolean }>),
  deletePhoto: (id: string) =>
    fetch(`/api/admin/photos/${id}`, { method: "DELETE" }).then(parseResponse<{ ok: boolean }>),
  importTargets: (csv: string, activityId = "") => {
    const suffix = activityId ? `?activityId=${encodeURIComponent(activityId)}` : "";
    return fetch(`/api/admin/targets/import${suffix}`, { method: "POST", body: csv }).then(parseResponse<{ imported: number }>);
  },
  codes: (activityId = "") => {
    const suffix = activityId ? `?activityId=${encodeURIComponent(activityId)}` : "";
    return fetch(`/api/admin/codes${suffix}`).then(parseResponse<{ codes: AccessCode[] }>);
  },
  createCode: (body: { activityId: string; label: string; code?: string; expiresAt?: string; maxUses?: number | null }) =>
    fetch("/api/admin/codes", { method: "POST", body: JSON.stringify(body) }).then(parseResponse<{ id: string; code: string; label: string; status: string }>),
  setCodeStatus: (id: string, action: "disable" | "enable" | "revoke") =>
    fetch(`/api/admin/codes/${id}/${action}`, { method: "POST" }).then(parseResponse<{ ok: boolean; status: string }>)
};
